/**
 * Market-access helpers.
 *
 * The hand-rolled fetch clients (getIndicativePrices / listDemands /
 * getBatchMatches) were repointed to the orval-generated market hooks
 * (TD-103): `useMarketPublicControllerGetPrices`, `useMarketControllerListDemands`,
 * `useMarketControllerGetMatches`. All three list/match endpoints return a
 * `{ data: [...] }` envelope, which the generated `IndicativePriceListDto` /
 * `BuyerDemandListDto` / `MatchListDto` models reflect — consumers read `.data`.
 *
 * This module now only holds `microToZarp`, a pure display formatter orval can't
 * model (monetary fields are micro-ZARP, 1e-6 ZARP, carried as STRINGS on the
 * wire to preserve precision).
 */

import { fseAxios } from './client';

/**
 * Convert a micro-ZARP wire string (1e-6 ZARP) to a whole-ZARP number for
 * display. Non-numeric / empty input yields 0 (caller decides how to render).
 * We divide by 1_000_000; the result is a plain number suitable for <Money>.
 */
export function microToZarp(micro: string): number {
  const n = Number(micro);
  if (!Number.isFinite(n)) return 0;
  return n / 1_000_000;
}

// --------------------------------------------------------------------------
// Build #8: price time series — orval-generated types (TD-124)
// --------------------------------------------------------------------------
//
// The public GET /market/prices/timeseries endpoint returns a bare
// PriceTimeSeriesDto object (NOT a { data } list envelope). The DTOs are now the
// single source of truth in the generated client (regen vs live /docs-json),
// re-exported here under the names the screens use. The client function below
// stays hand-rolled because it returns the unwrapped body the screens consume.
//
// MONEY: `micro` is a micro-ZARP (1e-6 ZARP) integer carried as a STRING on the
// wire to preserve precision — convert with microToZarp() for display only.

import type {
  BuyerDemandDto,
  PricePointDto,
  PriceTimeSeriesDto,
  MarketPublicControllerGetPriceTimeSeriesParams,
} from './generated/models';

export type PricePoint = PricePointDto;
export type PriceTimeSeries = PriceTimeSeriesDto;
export type PriceTimeSeriesParams = MarketPublicControllerGetPriceTimeSeriesParams;

/**
 * Fetch the public price time series for a product (default mango) over the
 * last `days` days. Returns the bare PriceTimeSeries object. Soft-fail at the
 * call site — the marketplace stat bar hides on error rather than blocking the
 * browse.
 */
export function getPriceTimeSeries(
  params: PriceTimeSeriesParams = {},
  signal?: AbortSignal,
): Promise<PriceTimeSeries> {
  return fseAxios<PriceTimeSeries>({
    url: '/market/prices/timeseries',
    method: 'GET',
    params,
    signal,
  });
}

// --------------------------------------------------------------------------
// Demand-loop: seller responses to buyer demands — HAND-ROLLED client (orval
// not regenerated for these endpoints yet; they ride the next central regen).
// All go through the shared `fseAxios` mutator (Bearer + 401-refresh +
// FseApiError envelope). Reuses the generated BuyerDemandDto for the demand
// shape; DemandResponse is defined here against the authoritative API DTO.
// MONEY: priceZarpPerKgMicro is micro-ZARP (1e-6 ZARP) as a STRING — convert with
// microToZarp() for display only; qtyKg is a numeric kg string. Both nullable.
// (BuyerDemandDto is imported with the other generated types at the top.)
// --------------------------------------------------------------------------

/** A seller's response on a buyer demand (GET /market/demands/:id/responses). */
export interface DemandResponse {
  id: string;
  demandId: string;
  responderUserId: string;
  /** The responder's display name (null if unset). */
  responderName: string | null;
  /** Proposed quantity in kg (numeric string), or null for bare interest. */
  qtyKg: string | null;
  /** Proposed micro-ZARP/kg (string), or null. */
  priceZarpPerKgMicro: string | null;
  /** Free-text message to the buyer, or null. */
  note: string | null;
  createdAt: string;
}

/** Seller "I can supply" request body — all fields optional. */
export interface RespondToDemandBody {
  qtyKg?: string;
  priceZarpPerKgMicro?: string;
  note?: string;
}

/**
 * Seller responds to a buyer demand. Roles: farmer/group_lead/coop_admin/admin.
 * 404 if the demand is unknown, 403 if it is no longer open or the caller owns it.
 */
export function respondToDemand(
  demandId: string,
  body: RespondToDemandBody,
): Promise<DemandResponse> {
  return fseAxios<DemandResponse>({
    url: `/market/demands/${demandId}/respond`,
    method: 'POST',
    data: body,
  });
}

/** The caller's OWN demands (all statuses), newest first. Buyer/admin. */
export function listMyDemands(signal?: AbortSignal): Promise<{ data: BuyerDemandDto[] }> {
  return fseAxios<{ data: BuyerDemandDto[] }>({
    url: '/market/demands/mine',
    method: 'GET',
    signal,
  });
}

/** The seller responses on a demand the caller owns (newest first). Buyer/admin. */
export function getDemandResponses(
  demandId: string,
  signal?: AbortSignal,
): Promise<{ data: DemandResponse[] }> {
  return fseAxios<{ data: DemandResponse[] }>({
    url: `/market/demands/${demandId}/responses`,
    method: 'GET',
    signal,
  });
}
